import React, { useCallback } from 'react';
import { ServicesContext } from '../ServicesContext';
import { PatientCacheRef, PatientServiceEvents } from './usePatientService';

export interface UpdateGapBaseParams {
    notes?: Partial<NoteType>[];
    tasks?: Pick<ReminderType, 'description' | 'due_at'>[];
    updated_at?: string;
}

type GapProp = 'risk_gaps' | 'foc_gaps' | 'quality_gaps';

interface UpdateGapConfig<TGap> {
    urlPrefix: string;
    gapProp: GapProp;
    transform?: (data: TGap) => TGap;
}

export function createUseUpdateGap<TParams extends UpdateGapBaseParams, TGap = GapType>(
    config: UpdateGapConfig<TGap>
) {
    return (events: PatientServiceEvents, patientCache: PatientCacheRef) => {
        const services = React.useContext(ServicesContext);

        const updateGap = useCallback(
            async (
                id: number,
                patientId: number | null,
                params: TParams,
                options?: ApiRequestOptionsType
            ) => {
                const result = await services.api.call(
                    {
                        method: 'put',
                        url: `${config.urlPrefix}${id}`,
                        data: params,
                    },
                    options
                );

                if (params.notes) {
                    services.events.publish(events.notes_updated);
                }
                if (params.tasks) {
                    services.events.publish(events.reminders_updated);
                }
                if (patientId !== null) {
                    const gaps = patientCache.current[patientId][
                        config.gapProp
                    ] as unknown as (TGap & { id: number })[];
                    gaps.forEach((gap, i, arr) => {
                        if (gap.id === id) {
                            arr[i] = config.transform ? config.transform(result.data) : result.data;
                        }
                    });
                    services.events.publish(events.patient_updated);
                }
            },
            [services.api, services.events, events, patientCache]
        );

        return updateGap;
    };
}
